import { useMemo, useState } from 'react'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import Tooltip from '../common/Tooltip'
import MeasureMomentQuestionsModal from './MeasureMomentQuestionsModal'
import summarizeMomentQuestions from '../../helpers/questionnaire/summarizeMomentQuestions'

// De VRAGEN-cel in de meetmomententabel. Toont in één oogopslag of dit
// meetmoment afwijkt van de volledige vragenlijst en opent de selectiemodal.
//
// Bewust een <button>: de tabel staat in ResearchBuilder binnen een
// ReadOnlyGuard (<fieldset disabled>), en die schakelt knoppen vanzelf uit —
// een MUI-icoon met onClick zou gewoon klikbaar blijven.
// AI-CONTEXT: See docs/patterns.md#hook-permission-gate

/**
 * @param {object} props
 * @param {object} props.moment - Het MeasureMoments-doc van deze rij
 * @param {Array<object>} props.fields - QuestionnaireFields van de gekoppelde
 *   vragenlijst, in Position-volgorde (inclusief sectiemarkeringen)
 * @param {Array<object>} [props.siblingMoments] - De andere meetmomenten van
 *   hetzelfde onderzoek, voor 'Overnemen van…' in de modal
 * @param {boolean} props.hasQuestionnaire - Of het ONDERZOEK een vragenlijst
 *   gekoppeld heeft. Niet af te leiden uit het meetmoment zelf: oudere
 *   meetmomenten dragen de `QuestionnaireID`-spiegeling niet.
 */
const MeasureMomentQuestionsCell = ({ moment, fields, siblingMoments = [], hasQuestionnaire }) => {
    // State
    const [modalOpen, setModalOpen] = useState(false)

    // Hooks
    const summary = useMemo(
        () => summarizeMomentQuestions(fields, moment?.ExcludedFieldIDs),
        [fields, moment?.ExcludedFieldIDs]
    )

    // Zonder vragenlijst valt er niets te kiezen — dat is een instelling van het
    // onderzoek, niet van dit meetmoment.
    // Zolang de vragenlijst nog binnenkomt is elke telling een gok — dan liever
    // niets beweren dan even 'Alle vragen (0)' tonen.
    if (!hasQuestionnaire || summary.total === 0) {
        return (
            <Tooltip
                content={hasQuestionnaire
                    ? 'Deze vragenlijst heeft nog geen vragen'
                    : 'Koppel eerst een vragenlijst aan dit onderzoek'}
                top="-45px"
            >
                <span className="mmq-cell-empty">–</span>
            </Tooltip>
        )
    }

    const isDefault = summary.active === summary.total
    const label = isDefault
        ? `Alle vragen (${summary.total})`
        : (summary.active === 0 ? 'Geen vragen' : `${summary.active} van ${summary.total} vragen`)

    const stateClass = isDefault
        ? 'mmq-cell-button'
        : (summary.active === 0 ? 'mmq-cell-button mmq-cell-empty-state' : 'mmq-cell-button mmq-cell-changed')

    return (
        <>
            <Tooltip content="Kies welke vragen op dit meetmoment worden gesteld" top="-45px">
                <button type="button" className={stateClass} onClick={() => setModalOpen(true)}>
                    <ChecklistOutlinedIcon fontSize="small" />
                    {label}
                </button>
            </Tooltip>

            {modalOpen && (
                <MeasureMomentQuestionsModal
                    modalOpen={modalOpen}
                    setModalOpen={setModalOpen}
                    moment={moment}
                    fields={fields}
                    siblingMoments={siblingMoments}
                />
            )}
        </>
    )
}

export default MeasureMomentQuestionsCell
